import type { Locale } from '../locales'

const zhCN = {
  release: {
    title: '最近更新版本',
    hint: '当前前端服务提供的版本；构建时间不代表部署完成时间。',
    version: '版本标识',
    builtAt: '构建时间',
    unavailable: '暂时无法读取版本信息，请刷新重试。',
  },
  errors: {
    request: '请求失败，请重试。',
    network: '网络连接失败，请重试。',
    unknown: '请求结果尚未确认，请先查询原请求，不要重复提交。',
    response: '服务响应无法核实，请重试读取。',
    identity: '账号已变化，请重新读取。',
    login: '请先登录，或重新登录后再试。',
    credentials: '邮箱或密码不正确。',
    email: '请输入有效的邮箱地址。',
    emailTaken: '此邮箱已注册，请登录或找回密码。',
    password: '密码不符合要求，请检查长度和字母、数字组合。',
    registration: '注册暂时未完成，请稍后重试。',
    verify: '请先验证邮箱，再进行此操作。',
    forbidden: '你没有执行此操作的权限。',
    notFound: '未找到所请求的内容。',
    conflict: '状态已变化，请读取最新状态后再操作。',
    rate: '操作过于频繁，请稍后再试。',
    quota: '当前可用额度已用完。',
    subscriptions: '订阅数量已达到当前额度。',
    paid: '此操作需要有效付费会员或管理员权限。',
    fileSize: '文件须为 1 字节至 200 MiB。',
    fileType: '仅支持 MP4 或 MPEG-TS；服务端会核验文件内容。',
    unavailable: '服务暂时不可用，请稍后重试。',
    expired: '链接或兑换码已失效，请核对或重新申请。',
    codeUsed: '此兑换码已被使用。',
    checkout: '支付地址无法核实，请重新查询原订单。',
    input: '输入不符合要求，请检查后重试。',
    confirm: '结果尚未核实，请继续查询原请求。',
  },
  ui: {
    notFound: '页面不存在',
    mainNavigation: '主导航',
    notifications: '通知',
    telegramSteps: '打开 Telegram 机器人并发送上方命令，完成后检查绑定状态。',
    unknown: '未知',
  },
  status: {
    queued: '排队中',
    running: '运行中',
    retry_scheduled: '等待重试',
    succeeded: '已完成',
    failed: '失败',
    dead_letter: '重试已耗尽',
    cancelled: '已取消',
    PENDING: '待支付',
    PAID: '已支付',
    CANCELLED: '已关闭',
    MANUAL: '手动开通',
    CDKEY: '兑换码',
    CCPAYMENT: 'CCPayment',
  },
} as const

const zhTW = {
  release: {
    title: '最近更新版本',
    hint: '目前前端服務提供的版本；建置時間不代表部署完成時間。',
    version: '版本識別碼',
    builtAt: '建置時間',
    unavailable: '暫時無法讀取版本資訊，請重新整理再試。',
  },
  errors: {
    request: '請求失敗，請重試。',
    network: '網路連線失敗，請重試。',
    unknown: '請求結果尚未確認，請先查詢原請求，勿重複送出。',
    response: '無法確認服務回應，請重新讀取。',
    identity: '帳號已變更，請重新讀取。',
    login: '請先登入，或重新登入後再試。',
    credentials: '電子郵件或密碼不正確。',
    email: '請輸入有效的電子郵件地址。',
    emailTaken: '此電子郵件已註冊，請登入或重設密碼。',
    password: '密碼不符合要求，請確認長度及英文字母、數字組合。',
    registration: '註冊暫時未完成，請稍後重試。',
    verify: '請先驗證電子郵件，再執行此操作。',
    forbidden: '你沒有執行此操作的權限。',
    notFound: '找不到要求的內容。',
    conflict: '狀態已變更，請讀取最新狀態後再操作。',
    rate: '操作過於頻繁，請稍後再試。',
    quota: '目前可用額度已用完。',
    subscriptions: '訂閱數量已達目前上限。',
    paid: '此操作需要有效付費會員或管理員權限。',
    fileSize: '檔案須為 1 位元組至 200 MiB。',
    fileType: '僅支援 MP4 或 MPEG-TS；伺服器會驗證檔案內容。',
    unavailable: '服務暫時無法使用，請稍後重試。',
    expired: '連結或兌換碼已失效，請確認或重新申請。',
    codeUsed: '此兌換碼已被使用。',
    checkout: '無法確認付款網址，請重新查詢原訂單。',
    input: '輸入不符合要求，請檢查後重試。',
    confirm: '結果尚未確認，請繼續查詢原請求。',
  },
  ui: {
    notFound: '頁面不存在',
    mainNavigation: '主導覽',
    notifications: '通知',
    telegramSteps: '開啟 Telegram 機器人並傳送上方指令，完成後確認綁定狀態。',
    unknown: '未知',
  },
  status: {
    queued: '排隊中',
    running: '執行中',
    retry_scheduled: '等待重試',
    succeeded: '已完成',
    failed: '失敗',
    dead_letter: '重試次數已用完',
    cancelled: '已取消',
    PENDING: '待付款',
    PAID: '已付款',
    CANCELLED: '已關閉',
    MANUAL: '手動開通',
    CDKEY: '兌換碼',
    CCPAYMENT: 'CCPayment',
  },
} as const

const en = {
  release: {
    title: 'Latest release',
    hint: 'Version served by the frontend. Build time is not deployment completion time.',
    version: 'Release ID',
    builtAt: 'Built at',
    unavailable: 'Version information is unavailable. Refresh to try again.',
  },
  errors: {
    request: 'The request failed. Please try again.',
    network: 'Connection failed. Please try again.',
    unknown: 'The result is unconfirmed. Check the original request before submitting again.',
    response: 'The server response could not be verified. Please reload.',
    identity: 'Your account has changed. Please reload.',
    login: 'Please sign in and try again.',
    credentials: 'Incorrect email or password.',
    email: 'Enter a valid email address.',
    emailTaken: 'This email is registered. Sign in or reset your password.',
    password: 'Check the password length and include letters and numbers.',
    registration: 'Registration could not be completed. Please try again later.',
    verify: 'Verify your email before continuing.',
    forbidden: 'You do not have permission to perform this action.',
    notFound: 'The requested item was not found.',
    conflict: 'The state has changed. Reload before continuing.',
    rate: 'Too many requests. Please try again later.',
    quota: 'Your available quota has been used up.',
    subscriptions: 'You have reached your subscription limit.',
    paid: 'This action requires an active paid membership or administrator access.',
    fileSize: 'The file must be between 1 byte and 200 MiB.',
    fileType: 'Only MP4 and MPEG-TS are supported. The server verifies the file contents.',
    unavailable: 'The service is temporarily unavailable. Please try again later.',
    expired: 'This link or code has expired. Check it or request a new one.',
    codeUsed: 'This redemption code has already been used.',
    checkout: 'The checkout address could not be verified. Check the original order again.',
    input: 'Check your input and try again.',
    confirm: 'The result is not yet verified. Check the original request again.',
  },
  ui: {
    notFound: 'Page not found',
    mainNavigation: 'Main navigation',
    notifications: 'Notifications',
    telegramSteps: 'Open the Telegram bot and send the command above, then check the binding status.',
    unknown: 'Unknown',
  },
  status: {
    queued: 'Queued',
    running: 'Running',
    retry_scheduled: 'Retry scheduled',
    succeeded: 'Completed',
    failed: 'Failed',
    dead_letter: 'Retries exhausted',
    cancelled: 'Cancelled',
    PENDING: 'Pending payment',
    PAID: 'Paid',
    CANCELLED: 'Closed',
    MANUAL: 'Manual activation',
    CDKEY: 'Redemption code',
    CCPAYMENT: 'CCPayment',
  },
} as const

const ja = {
  release: {
    title: '最新バージョン',
    hint: 'フロントエンドが配信中のバージョンです。ビルド日時はデプロイ完了日時ではありません。',
    version: 'バージョン ID',
    builtAt: 'ビルド日時',
    unavailable: 'バージョン情報を取得できません。更新して再試行してください。',
  },
  errors: {
    request: 'リクエストに失敗しました。もう一度お試しください。',
    network: '接続できませんでした。もう一度お試しください。',
    unknown: '結果を確認できません。再送信せず、元のリクエストの状態を確認してください。',
    response: 'サーバーの応答を確認できません。再読み込みしてください。',
    identity: 'アカウントが変更されました。再読み込みしてください。',
    login: 'ログインしてから、もう一度お試しください。',
    credentials: 'メールアドレスまたはパスワードが正しくありません。',
    email: '有効なメールアドレスを入力してください。',
    emailTaken: 'このメールアドレスは登録済みです。ログインするか、パスワードを再設定してください。',
    password: 'パスワードの長さを確認し、英字と数字を含めてください。',
    registration: '登録を完了できませんでした。しばらくしてから再試行してください。',
    verify: 'メールアドレスを確認してから操作してください。',
    forbidden: 'この操作を行う権限がありません。',
    notFound: '指定された内容が見つかりません。',
    conflict: '状態が変更されました。最新の状態を読み込んでから操作してください。',
    rate: 'リクエストが多すぎます。しばらくしてからお試しください。',
    quota: '利用可能な回数を使い切りました。',
    subscriptions: '購読数が上限に達しています。',
    paid: 'この操作には有効な有料会員資格または管理者権限が必要です。',
    fileSize: 'ファイルサイズは1バイト以上200 MiB以下にしてください。',
    fileType: 'MP4またはMPEG-TSのみ対応しています。ファイル内容はサーバーで検証されます。',
    unavailable: 'サービスを一時的に利用できません。しばらくしてからお試しください。',
    expired: 'リンクまたはコードの有効期限が切れています。確認するか、再発行してください。',
    codeUsed: 'この引き換えコードは使用済みです。',
    checkout: '決済先を確認できません。元の注文を再確認してください。',
    input: '入力内容を確認して、もう一度お試しください。',
    confirm: '結果はまだ確認できていません。元のリクエストを再確認してください。',
  },
  ui: {
    notFound: 'ページが見つかりません',
    mainNavigation: 'メインナビゲーション',
    notifications: '通知',
    telegramSteps: 'Telegramボットを開いて上のコマンドを送信し、連携状態を確認してください。',
    unknown: '不明',
  },
  status: {
    queued: '待機中',
    running: '実行中',
    retry_scheduled: '再試行待ち',
    succeeded: '完了',
    failed: '失敗',
    dead_letter: '再試行回数の上限に到達',
    cancelled: 'キャンセル済み',
    PENDING: '支払い待ち',
    PAID: '支払い済み',
    CANCELLED: '終了',
    MANUAL: '手動有効化',
    CDKEY: '引き換えコード',
    CCPAYMENT: 'CCPayment',
  },
} as const

const ko = {
  release: {
    title: '최근 업데이트 버전',
    hint: '프런트엔드가 제공하는 버전입니다. 빌드 시간은 배포 완료 시간이 아닙니다.',
    version: '버전 ID',
    builtAt: '빌드 시간',
    unavailable: '버전 정보를 불러올 수 없습니다. 새로고침해 주세요.',
  },
  errors: {
    request: '요청에 실패했습니다. 다시 시도해 주세요.',
    network: '연결에 실패했습니다. 다시 시도해 주세요.',
    unknown: '결과를 확인하지 못했습니다. 다시 제출하기 전에 기존 요청 상태를 확인해 주세요.',
    response: '서버 응답을 확인할 수 없습니다. 다시 불러와 주세요.',
    identity: '계정이 변경되었습니다. 다시 불러와 주세요.',
    login: '로그인한 후 다시 시도해 주세요.',
    credentials: '이메일 또는 비밀번호가 올바르지 않습니다.',
    email: '올바른 이메일 주소를 입력해 주세요.',
    emailTaken: '이미 등록된 이메일입니다. 로그인하거나 비밀번호를 재설정해 주세요.',
    password: '비밀번호 길이를 확인하고 영문자와 숫자를 포함해 주세요.',
    registration: '가입을 완료하지 못했습니다. 잠시 후 다시 시도하세요.',
    verify: '이메일 인증 후 진행해 주세요.',
    forbidden: '이 작업을 수행할 권한이 없습니다.',
    notFound: '요청한 항목을 찾을 수 없습니다.',
    conflict: '상태가 변경되었습니다. 최신 상태를 불러온 후 진행해 주세요.',
    rate: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
    quota: '사용 가능한 횟수를 모두 사용했습니다.',
    subscriptions: '구독 수가 한도에 도달했습니다.',
    paid: '이 작업에는 유효한 유료 멤버십 또는 관리자 권한이 필요합니다.',
    fileSize: '파일 크기는 1바이트 이상 200 MiB 이하여야 합니다.',
    fileType: 'MP4와 MPEG-TS만 지원합니다. 서버에서 파일 내용을 검증합니다.',
    unavailable: '서비스를 일시적으로 이용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
    expired: '링크 또는 코드가 만료되었습니다. 확인하거나 다시 요청해 주세요.',
    codeUsed: '이미 사용된 교환 코드입니다.',
    checkout: '결제 주소를 확인할 수 없습니다. 기존 주문을 다시 조회해 주세요.',
    input: '입력 내용을 확인하고 다시 시도해 주세요.',
    confirm: '결과가 아직 확인되지 않았습니다. 기존 요청을 다시 조회해 주세요.',
  },
  ui: {
    notFound: '페이지를 찾을 수 없습니다',
    mainNavigation: '주 메뉴',
    notifications: '알림',
    telegramSteps: 'Telegram 봇을 열어 위 명령어를 전송한 후 연결 상태를 확인해 주세요.',
    unknown: '알 수 없음',
  },
  status: {
    queued: '대기 중',
    running: '실행 중',
    retry_scheduled: '재시도 대기 중',
    succeeded: '완료',
    failed: '실패',
    dead_letter: '재시도 횟수 초과',
    cancelled: '취소됨',
    PENDING: '결제 대기 중',
    PAID: '결제 완료',
    CANCELLED: '종료',
    MANUAL: '수동 활성화',
    CDKEY: '교환 코드',
    CCPAYMENT: 'CCPayment',
  },
} as const

export const supplementalMessages = { 'zh-CN': zhCN, 'zh-TW': zhTW, en, ja, ko } satisfies Record<
  Locale,
  { [K in keyof typeof zhCN]: { [P in keyof (typeof zhCN)[K]]: string } }
>
export type SupplementalMessages = typeof zhCN
